import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { ordersApi, type Order } from '@/lib/api';
import { useAuthStore } from '@/store/useAuthStore';
import { StatusBadge } from '@/components/ui/StatusBadge';
import { Button } from '@/components/ui/Button';
import { formatDate, formatPrice } from '@/lib/utils';

const EDITABLE_STATUSES = ['pending-album', 'album-submitted'];

export default function DashboardPage() {
  const { user, profile, signOut } = useAuthStore();
  const navigate = useNavigate();

  // The team has its own workspace — admin → /admin, staff → /admin/orders —
  // never the customer « Mes commandes » view. Replace history so the back
  // button doesn't bounce them straight back here.
  const teamHome = profile?.role === 'admin' ? '/admin' : profile?.role === 'staff' ? '/admin/orders' : null;
  useEffect(() => {
    if (teamHome) navigate(teamHome, { replace: true });
  }, [teamHome, navigate]);

  // ⚠️ ONLY FOR SOMEONE THE SERVER SAID IS A CUSTOMER. « Not admin » used to be
  // enough, so an admin whose profile had not loaded fetched « my orders » and
  // got « Erreur lors du chargement des commandes » (2026-09-25).
  const { data, isLoading, error } = useQuery({
    queryKey: ['my-orders'],
    queryFn: () => ordersApi.mine(),
    enabled: !!user && profile?.role === 'customer',
  });

  useEffect(() => {
    document.title = 'Mon espace — Storiz';
  }, []);

  // While the redirect is in flight, don't flash the customer dashboard.
  if (teamHome) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-brand-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-100 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/storiz-logo.svg" alt="Storiz" className="h-8" onError={(e) => {
              const el = e.target as HTMLImageElement;
              el.style.display = 'none';
              el.nextElementSibling?.classList.remove('hidden');
            }} />
            <span className="font-bold text-lg text-gray-900 hidden">Storiz</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm text-gray-500 hidden sm:block">
              {profile?.full_name || user?.email}
            </span>
            <Button variant="ghost" size="sm" onClick={() => signOut()}>
              Déconnexion
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        <h1 className="text-2xl font-bold text-gray-900 mb-1">Mes commandes</h1>
        <p className="text-gray-500 text-sm mb-6">
          Créez et gérez vos albums photo personnalisés
        </p>

        {isLoading && (
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="bg-white rounded-xl h-24 animate-pulse border border-gray-100" />
            ))}
          </div>
        )}

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm">
            Erreur lors du chargement des commandes.
          </div>
        )}

        {!isLoading && data?.orders?.length === 0 && (
          <div className="text-center py-16 bg-white rounded-2xl border border-gray-100">
            <div className="text-5xl mb-4">📦</div>
            <h3 className="text-lg font-semibold text-gray-800 mb-2">
              Aucune commande trouvée
            </h3>
            <p className="text-gray-500 text-sm max-w-sm mx-auto">
              Passez votre commande sur{' '}
              <a href="https://storiz.ma" className="text-brand-700 hover:underline">
                storiz.ma
              </a>{' '}
              et revenez ici pour créer votre album.
            </p>
          </div>
        )}

        <div className="space-y-3">
          {data?.orders?.map((order: Order) => (
            <OrderCard key={order.id} order={order} />
          ))}
        </div>
      </main>
    </div>
  );
}

function OrderCard({ order }: { order: Order }) {
  const isEditable = EDITABLE_STATUSES.includes(order.status);
  const destinations = [order.destination_1, order.destination_2, order.destination_3]
    .filter(Boolean)
    .join(' · ');

  return (
    <div className="bg-white rounded-xl border border-gray-100 p-5 flex items-center gap-4">
      {/* Icon */}
      <div className="w-12 h-12 rounded-xl bg-brand-50 flex items-center justify-center text-xl flex-shrink-0">
        📸
      </div>

      {/* Info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap mb-1">
          <span className="font-semibold text-gray-900 text-sm">
            {order.shopify_order_name}
          </span>
          {order.bundle && (
            <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">
              {order.bundle}
            </span>
          )}
          <StatusBadge status={order.status} />
        </div>
        <p className="text-sm text-gray-500 truncate">
          {destinations || 'Titres non définis'}
          {order.year_1 && ` · ${order.year_1}`}
        </p>
        <p className="text-xs text-gray-400 mt-0.5">
          {formatDate(order.created_at)} · {formatPrice(order.total_price)}
        </p>
      </div>

      {/* Action */}
      <div className="flex-shrink-0">
        {isEditable ? (
          <Link to={`/flow/${order.shopify_order_id}`}>
            <Button size="sm">Créer mon album</Button>
          </Link>
        ) : (
          <Button variant="secondary" size="sm" disabled>
            {order.status === 'album-submitted' ? 'Soumis' : 'Voir'}
          </Button>
        )}
      </div>
    </div>
  );
}
